import { track } from "@amplitude/analytics-react-native";
import toast from "src/swsh-native/toast";
import { useCallback } from "react";
import { amp } from "src/api/amplitude";
import RemoveGatheringUsersDocument from "src/api/graphql/__generated__/documents/RemoveGatheringUsersDocument";
import SetGatheringUsersRelationshipDocument from "src/api/graphql/__generated__/documents/SetGatheringUsersRelationshipDocument";
import GatheringRelationshipEnum from "src/api/graphql/__generated__/enums/GatheringRelationshipEnum";
import { useAsyncMutation } from "src/api/graphql/helper";
import log from "src/helpers/log";
import toastError from "src/helpers/toastError";
import { UseGatheringAndUsers_User } from "src/hooks/useGatheringAndUsers";
import { alert } from "src/swsh-native";

const useGatheringViewerOptions = ({
	gatheringId,
	user,
	onAction,
}: {
	gatheringId: string;
	user: UseGatheringAndUsers_User;
	onAction: undefined | (() => void);
}) => {
	const setGatheringUsersRelationship = useAsyncMutation(SetGatheringUsersRelationshipDocument);
	const removeGatheringUsers = useAsyncMutation(RemoveGatheringUsersDocument);

	const handleRemoveFromGathering = useCallback(async () => {
		track(amp.gathering.viewerOptions.removeUser.initiate);
		const name =
			user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : "user";
		alert({
			title: `Remove ${name} from album?`,
			message: `All photos uploaded by ${name} will be removed from the album.`,
			buttons: [
				{
					style: "cancel",
					isPreferred: true,
					text: "Cancel",
					onPress: () => {
						track(amp.gathering.viewerOptions.removeUser.cancel);
					},
				},
				{
					style: "destructive",
					text: "Remove",
					onPress: async () => {
						track(amp.gathering.viewerOptions.removeUser.confirm);
						try {
							const { data } = await removeGatheringUsers({
								input: {
									gatheringId,
									userIds: [user.userId],
								},
							});
							if (!data?.removeGatheringUsers?.success) {
								log.error("Failed to remove user from gathering");
								toastError();
							} else {
								toast({
									preset: "done",
									title: "Success",
									message: `Removed ${name}`,
								});
							}
							onAction?.();
						} catch (err) {
							log.error(err, "Failed to remove user from gathering");
							toastError();
						}
					},
				},
			],
		});
	}, [gatheringId, onAction, removeGatheringUsers, user.firstName, user.lastName, user.userId]);

	const handleDemoteAdmin = useCallback(async () => {
		track(amp.gathering.viewerOptions.demoteAdmin.initiate);
		const name =
			user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : "user";
		alert({
			title: `Remove ${name} as a cohost?`,
			message: `${name} will no longer be a cohost of the album`,
			buttons: [
				{
					style: "cancel",
					isPreferred: true,
					text: "Cancel",
					onPress: () => {
						track(amp.gathering.viewerOptions.demoteAdmin.cancel);
					},
				},
				{
					style: "destructive",
					text: "Demote",
					onPress: async () => {
						track(amp.gathering.viewerOptions.demoteAdmin.confirm);
						try {
							const { data } = await setGatheringUsersRelationship({
								input: {
									gatheringId,
									relationship: GatheringRelationshipEnum.Participant,
									userIds: [user.userId],
								},
							});
							if (!data?.setGatheringUsersRelationship?.success) {
								log.error("Failed to demote user from admin");
								toastError();
							} else {
								toast({
									preset: "done",
									title: "Success",
									message: `Removed cohost`,
								});
							}
							onAction?.();
						} catch (err) {
							log.error({ err }, "Failed to demote user from admin");
							toastError();
						}
					},
				},
			],
		});
	}, [
		gatheringId,
		onAction,
		setGatheringUsersRelationship,
		user.firstName,
		user.lastName,
		user.userId,
	]);

	const handlePromoteAdmin = useCallback(async () => {
		track(amp.gathering.viewerOptions.promoteAdmin);

		try {
			const { data } = await setGatheringUsersRelationship({
				input: {
					gatheringId,
					relationship: GatheringRelationshipEnum.Admin,
					userIds: [user.userId],
				},
			});
			if (!data?.setGatheringUsersRelationship?.success) {
				log.error("Failed to promote user to admin");
				toastError();
			} else {
				toast({
					preset: "done",
					title: "Success",
					message: "Added cohost",
				});
			}
			onAction?.();
		} catch (err) {
			log.error({ err }, "Failed to promote user to admin");
			toastError();
		}
	}, [gatheringId, onAction, setGatheringUsersRelationship, user.userId]);

	return {
		handleRemoveFromGathering,
		handleDemoteAdmin,
		handlePromoteAdmin,
	};
};
export default useGatheringViewerOptions;
